import { type RemixiconComponentType } from '@remixicon/react';
import { type ComponentPropsWithRef, type Ref } from 'react';

import { classNames } from '../../internal/classNames';

// Kit icons that Remix doesn't have. Styled by icon.scss (CSS masks over the SVGs in assets/icons).
export const PLATFORM_ICONS = [
    'crunchbase',
    'loading',
    'mercury',
    'phone',
    'phone-slash',
    'sso-google',
] as const;

export type PlatformIconName = (typeof PLATFORM_ICONS)[number];

/** A Remix icon component, imported directly from `@remixicon/react` so only used icons are bundled. */
export type RemixIcon = RemixiconComponentType;

/** What `Icon` can render: a Remix icon component or the name of a kit (Platform) icon. */
export type IconSource = RemixIcon | PlatformIconName;

type BaseIconProps = {
    className?: string;
    size?: number;
};

type RemixIconElementProps = Omit<
    ComponentPropsWithRef<RemixIcon>,
    'children' | 'className' | 'size' | 'ref'
>;

type PlatformIconElementProps = Omit<
    ComponentPropsWithRef<'span'>,
    'children' | 'className' | 'ref'
>;

export type IconProps =
    | ({ icon: RemixIcon; ref?: Ref<SVGSVGElement> } & BaseIconProps & RemixIconElementProps)
    | ({ icon: PlatformIconName; ref?: Ref<HTMLSpanElement> } & BaseIconProps &
          PlatformIconElementProps);

export const isPlatformIcon = (icon: IconSource): icon is PlatformIconName =>
    typeof icon === 'string';

const Icon = ({ icon, className = '', size = 16, ref, ...rest }: IconProps) => {
    if (!isPlatformIcon(icon)) {
        const ReIcon = icon;
        return (
            <ReIcon
                {...(rest as RemixIconElementProps)}
                size={size}
                className={classNames({
                    'ui-icon': true,
                    [className]: Boolean(className),
                })}
                ref={ref as Ref<SVGSVGElement>}
            />
        );
    }

    const { style, ...platformRest } = rest as PlatformIconElementProps;

    return (
        <span
            {...platformRest}
            ref={ref as Ref<HTMLSpanElement>}
            data-icon-name={icon}
            className={classNames({
                [`ui-icon icon icon-${icon}`]: true,
                [className]: Boolean(className),
            })}
            style={{ ...style, width: `${size}px`, height: `${size}px` }}
        />
    );
};

Icon.displayName = 'Icon';

export default Icon;
